// Single source of truth for akku-24.hu site + business identity.
// Used by <Seo>, the JSON-LD builders and page metadata — the same shape as
// akkubolt/frontend/src/data/businessInfo.ts (the two SPAs share one SEO
// architecture; keep structural changes in sync).
//
// Canonical host decision (SEO audit 2026-06-21): non-www https, param-free.
//
// CSR note: these strings reach Googlebot via JS-rendering today. They reach
// non-JS AI crawlers (GPTBot/OAI-SearchBot/PerplexityBot/ClaudeBot) once the
// build-time prerender step captures the rendered <head> into raw HTML.

export const SITE_ORIGIN = 'https://akku-24.hu';
export const SITE_NAME = 'Akku 24+';

// Default Open Graph image — the Vágóhíd store photo already used by the
// static shell's og:image; swap for a dedicated 1200x630 asset later.
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/images/stores/store-vagohid.jpg`;

// The single published line for the site (the network line).
export const PHONES_DISPLAY = ['+36 30 626 6000'] as const;

// Blog images are served by nginx from /images/blog/ (alias to app/public/images/blog/).
const BLOG_IMAGE_BASE = `${SITE_ORIGIN}/images/blog/`;

/**
 * Absolute blog-article image URL. The API returns null for most legacy
 * articles; a bare filename gets the /images/blog/ base, an absolute URL
 * passes through, and null falls back to the default OG image.
 */
export function blogImageUrl(image?: string | null): string {
	if (!image) return DEFAULT_OG_IMAGE;
	if (/^https?:\/\//i.test(image)) return image;
	return `${BLOG_IMAGE_BASE}${image.replace(/^\/+/, '')}`;
}

/**
 * Sitewide Organization / AutomotiveBusiness / LocalBusiness JSON-LD, emitted
 * once from Layout so it lands on every page. Mirrors the static block in
 * index.html (which carries data-rh so this node replaces it on hydration) —
 * change both together.
 *
 * Address stays city-only (as published) until the GATE-2 fix-or-fold decision
 * resolves the akku-24 NAP divergence (ÁSZF/Privacy name TIPCOOP Kft., Váci út 8).
 */
export function buildOrganizationLd(): Record<string, unknown> {
	return {
		'@context': 'https://schema.org',
		'@type': ['AutomotiveBusiness', 'LocalBusiness'],
		'@id': `${SITE_ORIGIN}/#organization`,
		name: SITE_NAME,
		url: SITE_ORIGIN,
		sameAs: ['https://www.facebook.com/akku24'],
		image: DEFAULT_OG_IMAGE,
		telephone: [...PHONES_DISPLAY],
		inLanguage: 'hu',
		address: {
			'@type': 'PostalAddress',
			addressLocality: 'Budapest',
			addressCountry: 'HU',
		},
		areaServed: { '@type': 'City', name: 'Budapest' },
		openingHoursSpecification: {
			'@type': 'OpeningHoursSpecification',
			dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
			opens: '08:00',
			closes: '24:00',
		},
	};
}
